import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { getContactUsData, updateContactData } from "../../../../services/CommonService";
import moment from 'moment';
import { exportToExcel } from '../../../../Utils/common-Excel';

const ContactManager = () => {
    const [contacts, setContacts] = useState([]);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState(moment().subtract(15, 'days').format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(moment().format('YYYY-MM-DD'));
    const [lookup, setLookup] = useState([]);
    const [statusData, setStatusData] = useState("1");
    const [dateError, setDateError] = useState("");
    const itemsPerPage = 5;

    useEffect(() => {
        fetchContacts();
        const lookup = JSON.parse(localStorage.getItem('lookup'));
        setLookup(lookup);
    }, []);

    // excel download
    const handleDownloadExcel = () => {
        const exportData = filteredContacts.map(item => {
            const statusLabel = lookup?.find(
                (m) => m.Flag === "12" && item.status == m.LookupCode
            )?.LookupValue || "Unknown"; // Fallback if not found

            return {
                Name: item.name,
                Email: item.email,
                Mobile: item.mobile,
                Description: item.description,
                Status: statusLabel,
                'Created on': formatDate(item.created_at),
                'Addressed on': formatDate(item.updated_at),
            };
        });
        exportToExcel(exportData, 'Contact', 'Contact', 'Contact List Report', startDate, endDate);
    };

    //fetch contact data
    const fetchContacts = async () => {
        try {
            const response = await getContactUsData();
            if (!response.status == 200) {
                throw new Error('Failed to fetch contacts');
            }

            const data = response.data;
            setContacts(data);
        } catch (err) {
            setError(err.message);
            Swal.fire({
                title: "Error",
                text: err?.response?.data?.message || err?.message,
                icon: "error",
                confirmButtonText: "OK",
            });
        }
    };

    // Format date to a more readable format
    const formatDate = (dateString) => {
        if (dateString) {
            const date = moment(dateString).format('DD-MM-YYYY');
            return date;
        } else return ''
    };

    const handleStartDateChange = (e) => {
        const value = e.target.value;
        setStartDate(value);
        setCurrentPage(1);

        if (endDate && new Date(value) > new Date(endDate)) {
            setDateError("Start date cannot be after end date.");
        } else {
            setDateError("");
        }
    };

    const handleEndDateChange = (e) => {
        const value = e.target.value;
        setEndDate(value);
        setCurrentPage(1);

        if (startDate && new Date(startDate) > new Date(value)) {
            setDateError("End date cannot be before start date.");
        } else {
            setDateError("");
        }
    };

    // Filter contacts based on search term and date range
    const filteredContacts = contacts.filter(contact => {
        const matchesSearch = (
            contact?.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            contact?.email?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            contact?.mobile?.includes(searchTerm) ||
            contact?.description?.toLowerCase().includes(searchTerm?.toLowerCase())
        );

        const contactDate = moment(contact.created_at);
        const isInDateRange = contactDate.isSameOrAfter(moment(startDate).startOf('day')) &&
            contactDate.isSameOrBefore(moment(endDate).endOf('day'));
        const matchesStatus = statusData === "" || contact.status == statusData;
        return matchesSearch && isInDateRange && matchesStatus;
    });

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentContacts = filteredContacts.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredContacts.length / itemsPerPage);

    // Generate page numbers
    const pageNumbers = [];
    for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
    }
    // Handle delete contact
    const updateStatus = async (item) => {
        const data = {
            status: 2
        }
        const response = await updateContactData(data, item.id);
        if (response.status == 200) {
            fetchContacts();
            Swal.fire(
                {
                    title: 'Success!',
                    text: "Status updated successfully.",
                    icon: 'success',
                }
            );
        }
    };

    if (error) {
        return (
            <div className="alert alert-danger" role="alert">
                Error: {error}
            </div>
        );
    }

    return (
        <div className="container-fluid mt-4">
            <div className="card shadow">
                <div className="card-header adminCardHeader text-white">
                    <h4 className="mb-0">Contacted People</h4>
                </div>
                <div className="card-body">
                    <div className="row mb-3">
                        <div className="col-md-5">
                            <div className="d-flex justify-content-start align-items-center">
                                <label className="me-2">Search:</label>
                                <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value)
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: '150px' }}
                                />
                            </div>
                        </div>
                        <div className='col-md-4'>
                            <div className='row'>
                                <div className="col-md-6">
                                    <div className="input-group align-items-center">
                                        <label className="me-2">From:</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={startDate}
                                            onChange={handleStartDateChange}
                                            style={{ padding: '10px' }}
                                        />
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="input-group align-items-center">
                                        <label className="me-2">To:</label>
                                        <input
                                            type="date"
                                            className="form-control form-control-sm"
                                            value={endDate}
                                            onChange={handleEndDateChange}
                                            style={{ padding: '10px' }}
                                        />
                                    </div>
                                </div>
                                {dateError && (
                                    <div className="col-12">
                                        <small className="text-danger">{dateError}</small>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="col-md-2">
                            <div className="input-group align-items-center">
                                <label className="me-2">Status</label>
                                <select
                                    className="form-select form-select-sm"
                                    style={{ width: "50px" }}
                                    value={statusData}
                                    onChange={(e) => {
                                        setStatusData(e.target.value);
                                        setCurrentPage(1);
                                    }}
                                >
                                    {lookup?.length && (lookup?.filter((m) => (m?.Flag === "12"))?.map((m) => (
                                        <option value={m.LookupCode} key={m.LookupCode}>{m.LookupValue}</option>
                                    )))}
                                </select>
                            </div>
                        </div>
                        <div className="col-md-1">
                            <div className="d-flex justify-content-end align-items-center">
                                <button className="btn btn-success mb-2" onClick={handleDownloadExcel}>
                                    <i className="bi bi-file-earmark-excel-fill me-2"></i>
                                    Excel
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th className='text-center'>S.No</th>
                                    <th>Name</th>
                                    <th>Email</th>
                                    <th className='text-center'>Mobile</th>
                                    <th>Description</th>
                                    <th>Status</th>
                                    <th className='text-center'>Created On</th>
                                    {(statusData === '2') && <th className='text-center'>Addressed On</th>}
                                    {(statusData === '1') && <th className='text-center'>Action</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {currentContacts.length > 0 ? (
                                    currentContacts.map((contact, i) => (
                                        <tr key={contact.id}>
                                            <td className='text-center'>{i + 1}</td>
                                            <td>{contact.name}</td>
                                            <td>{contact.email}</td>
                                            <td className='text-center'>{contact.mobile}</td>
                                            <td>{contact.description}</td>
                                            <td>{lookup?.find((m) => m.Flag === "12" && contact.status == m.LookupCode)?.LookupValue}</td>
                                            <td className='text-center'>{formatDate(contact.created_at)}</td>
                                            {(statusData === '2') && <td className='text-center'>{formatDate(contact.updated_at)}</td>}
                                            {(statusData === '1') && <td className='text-center'>
                                                <img src="/assets/images/check.png" style={{ width: '30px', height: '30px', cursor: 'pointer' }}
                                                    onClick={(e) =>
                                                        updateStatus(contact, e.target.checked ? 2 : 1)
                                                    } />
                                            </td>}
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center">No contacts found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <nav aria-label="Contact pagination">
                            <ul className="pagination pagination-sm justify-content-end">
                                <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                    >
                                        Previous
                                    </button>
                                </li>
                                {pageNumbers.map(number => (
                                    <li
                                        key={number}
                                        className={`page-item ${currentPage === number ? 'active' : ''}`}
                                    >
                                        <button
                                            className="page-link"
                                            onClick={() => setCurrentPage(number)}
                                        >
                                            {number}
                                        </button>
                                    </li>
                                ))}

                                <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                                    <button
                                        className="page-link"
                                        onClick={() => setCurrentPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                    >
                                        Next
                                    </button>
                                </li>
                            </ul>
                        </nav>
                    )}
                    <div className="text-center text-muted mt-3">
                        {filteredContacts.length > 0 ? (
                            <small>Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredContacts.length)} of {filteredContacts.length} contacts</small>
                        ) : (
                            <small>No contacts to display</small>
                        )}
                    </div>
                </div>
            </div>
        </div >
    );
};

export default ContactManager;